import classNames from 'classnames';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AdTagPreview,
  EmptyState,
  FacebookNewsfeedPreview,
  FacebookStoryPreview,
  InstagramFeedPreview,
  InstagramStoryPreview,
  Spinner,
} from '@feathr/components';

import type { TSocialPreviewType } from '../SocialPreviewTypeSelector';
import SocialPreviewTypeSelector from '../SocialPreviewTypeSelector';
import ZoomControls from '../ZoomControls';
import { usePreviewPanel } from './PreviewPanel.hooks';

import * as styles from './PreviewPanel.css';

export interface IPreviewPanelProps {
  /** Ad tag HTML markup for ad tag creatives. */
  adTagHtml?: string;
  /** The creative image URL for display creatives. */
  imageUrl?: string;
  /** Whether this is an ad tag creative. */
  isAdTag?: boolean;
  /** Whether this is a Meta creative. */
  isMetaCreative?: boolean;
  /** Natural width of the creative. */
  contentWidth?: number;
  /** Natural height of the creative. */
  contentHeight?: number;
  /** Creative name for alt text. */
  creativeName?: string;
  /** Whether frame extraction failed for a video creative. */
  hasVideoThumbnailError?: boolean;
  /** Whether a video thumbnail is currently being extracted. */
  isVideoThumbnailLoading?: boolean;
  className?: string;
}

const PLACEHOLDER_PAGE_NAME = 'Your Page';
const PLACEHOLDER_HEADLINE = 'Headline';
const PLACEHOLDER_WEBSITE_URL = 'example.com';

function renderSocialPreview(
  socialPreviewType: TSocialPreviewType,
  imageUrl: string,
  t: TFunction,
): JSX.Element {
  const commonProps = {
    imageUrl,
    pageName: PLACEHOLDER_PAGE_NAME,
    t,
  };

  switch (socialPreviewType) {
    case 'facebook_story':
      return <FacebookStoryPreview {...commonProps} />;

    case 'facebook_newsfeed':
      return (
        <FacebookNewsfeedPreview
          {...commonProps}
          headline={PLACEHOLDER_HEADLINE}
          websiteUrl={PLACEHOLDER_WEBSITE_URL}
        />
      );

    case 'instagram_feed':
      return <InstagramFeedPreview {...commonProps} />;

    case 'instagram_story':
      return <InstagramStoryPreview {...commonProps} />;
  }
}

function PreviewPanel({
  adTagHtml,
  imageUrl,
  isAdTag,
  isMetaCreative,
  isVideoThumbnailLoading,
  hasVideoThumbnailError,
  contentWidth,
  contentHeight,
  creativeName,
  className,
}: Readonly<IPreviewPanelProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    zoom,
    socialPreviewType,
    viewportRef,
    contentRef,
    handleZoomChange,
    handleZoomToFit,
    handleSocialPreviewTypeChange,
  } = usePreviewPanel({ contentWidth, contentHeight });

  function renderPreview(): JSX.Element {
    if (hasVideoThumbnailError || (!imageUrl && !isVideoThumbnailLoading && !isAdTag)) {
      return (
        <EmptyState
          description={t('Upload an image to see a preview here.')}
          label={t('No preview available')}
        />
      );
    }

    if (isAdTag) {
      return (
        <AdTagPreview
          height={contentHeight ?? 250}
          html={adTagHtml ?? ''}
          scale={zoom / 100}
          width={contentWidth ?? 300}
        />
      );
    }

    if (isMetaCreative && imageUrl) {
      return renderSocialPreview(socialPreviewType, imageUrl, t);
    }

    return (
      <>
        {isVideoThumbnailLoading && <Spinner />}
        <img
          alt={creativeName}
          className={classNames(styles.image, isVideoThumbnailLoading && styles.hidden)}
          src={imageUrl}
        />
      </>
    );
  }

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.toolbar}>
        {isMetaCreative && (
          <SocialPreviewTypeSelector
            onChange={handleSocialPreviewTypeChange}
            value={socialPreviewType}
          />
        )}
        <ZoomControls
          className={styles.zoomControls}
          onZoomChange={handleZoomChange}
          onZoomToFit={handleZoomToFit}
          zoom={zoom}
        />
      </div>
      <div className={styles.viewport} ref={viewportRef}>
        {isAdTag ? (
          <div className={styles.adTagContainer} ref={contentRef}>
            {renderPreview()}
          </div>
        ) : (
          <div className={styles.content} ref={contentRef}>
            {renderPreview()}
          </div>
        )}
      </div>
    </div>
  );
}

export default PreviewPanel;
